import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaUserCog, FaUsers, FaCalendarAlt, FaMoneyBill, FaCalendarCheck, FaChevronDown, FaSignOutAlt, FaBars, FaTimes, FaUser, FaCamera, FaIdCard, FaBriefcase, FaFileAlt, FaClipboardList, FaUserSecret, FaExternalLinkAlt } from 'react-icons/fa';
import { useAuth } from '@/contexts/AuthContext';
import { employeeService, Employee } from '@/services/employeeService';
import { AuthenticatedImage } from '@/components/AuthenticatedImage';
import { authService } from '@/services/authService';
import toast from 'react-hot-toast';

import { EMPLOYEES_BASIC_SCREEN, EMPLOYEES_FULL_SCREEN } from '@/utils/access';

// Navigation links with their corresponding screen codes for permission checking
const navLinks = [
  { name: 'Roles', path: '/roles', icon: <FaUserCog />, screenCode: 'roles_management', adminOnly: true },
  {
    name: 'Employee',
    path: '/employees',
    icon: <FaUsers />,
    screenCodes: [EMPLOYEES_FULL_SCREEN, EMPLOYEES_BASIC_SCREEN],
    adminOnly: false,
  },
  { name: 'FaceID Setup', path: '/face-detection', icon: <FaCamera />, screenCode: 'faceid_setup', adminOnly: false },
  { name: 'Employee Attendance', path: '/employee-attendance', icon: <FaIdCard />, screenCode: 'attendance_management', adminOnly: false },
  { name: 'Leaves', path: '/leaves', icon: <FaCalendarAlt />, screenCode: 'leaves_management', adminOnly: false },
  { name: 'Payroll', path: '/payroll', icon: <FaMoneyBill />, screenCode: 'payroll_management', adminOnly: false },
  { name: 'Schedule', path: '/schedule', icon: <FaCalendarCheck />, screenCode: 'schedules_management', adminOnly: false },
  { name: 'Job Descriptions', path: '/job-descriptions', icon: <FaFileAlt />, screenCode: 'job_descriptions_management', adminOnly: false },
  { name: 'Job Postings', path: '/job-postings', icon: <FaBriefcase />, screenCode: 'job_postings_management', adminOnly: false },
  { name: 'Job Applications Management', path: '/job-applications-management', icon: <FaClipboardList />, screenCode: 'job_applications_management', adminOnly: false },
];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { user, logout, impersonate, stopImpersonating } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [impersonateOptions, setImpersonateOptions] = useState<Employee[]>([]);
  const [selectedImpersonateId, setSelectedImpersonateId] = useState('');
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [clubCornersLoading, setClubCornersLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Check if user is admin (memoized)
  const isAdmin = useMemo(() => {
    if (!user) return false;
    
    // Check if roles array exists and contains Administrator role
    // Using type assertion for roles property that may exist at runtime
    const userWithRoles = user as typeof user & { roles?: Array<{ name?: string } | string> };
    if (userWithRoles.roles && Array.isArray(userWithRoles.roles)) {
      const hasAdminRole = userWithRoles.roles.some((role: { name?: string } | string) => {
        const roleName = typeof role === 'string' ? role : role?.name;
        const roleLower = roleName?.toLowerCase()?.trim();
        return roleLower === 'administrator' || roleLower === 'admin';
      });
      if (hasAdminRole) {
        return true;
      }
    }
    
    // Check if user.role string is Administrator (case-insensitive)
    if (user.role && typeof user.role === 'string') {
      const roleLower = user.role.toLowerCase().trim();
      if (roleLower === 'administrator' || roleLower === 'admin') {
        return true;
      }
    }
    
    // Check if employeeId is '0001' (default admin user)
    const employeeIdStr = String(user.employeeId || '');
    if (employeeIdStr === '0001' || employeeIdStr === '1') {
      return true;
    }
    
    return false;
  }, [user]);

  // Filter navigation links based on user permissions
  const visibleNavLinks = useMemo(() => {
    if (!user) return [];

    return navLinks.filter((link) => {
      if (link.adminOnly) {
        return isAdmin;
      }
      if (isAdmin) {
        return true;
      }
      const userScreenAccess = user.screenAccess || user.screens || [];
      if ('screenCodes' in link && link.screenCodes?.length) {
        return link.screenCodes.some((code) => userScreenAccess.includes(code));
      }
      return Boolean(link.screenCode && userScreenAccess.includes(link.screenCode));
    });
  }, [user, isAdmin]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const canImpersonate = Boolean(isAdmin && !user?.impersonatedBy);
  const isImpersonating = Boolean(user?.impersonatedBy);

  useEffect(() => {
    if (!canImpersonate) {
      return;
    }
    employeeService.getAllEmployees()
      .then((employees) => {
        setImpersonateOptions(employees.filter((emp) => emp.id !== user?.id && emp.status !== 'inactive' && emp.status !== 'terminated'));
      })
      .catch(() => {
        setImpersonateOptions([]);
      });
  }, [canImpersonate, user?.id]);

  const handleImpersonate = async () => {
    if (!selectedImpersonateId) {
      return;
    }
    try {
      setImpersonateLoading(true);
      await impersonate(selectedImpersonateId);
    } finally {
      setImpersonateLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
  };

  const handleOpenClubCorners = async () => {
    if (clubCornersLoading) return;
    try {
      setClubCornersLoading(true);
      const result = await authService.issueSsoCode({
        sourceApp: 'HRMS',
        targetApp: 'CLUB_CORNERS',
      });
      if (!result?.redirectUrl) {
        throw new Error('SSO issue did not return a redirect URL');
      }
      // One-time code only — never put the long-lived JWT in the URL
      window.location.assign(result.redirectUrl);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to open Club Corners';
      toast.error(message);
      setClubCornersLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        ref={sidebarRef}
        className={`fixed lg:static inset-y-0 left-0 w-64 bg-white border-r flex flex-col transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-16 flex items-center justify-between px-6 border-b">
          <div className="font-bold text-xl text-blue-600">HRMS</div>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {visibleNavLinks.map(link => (
            <Link
              key={link.name}
              to={link.path}
              className={`flex items-center px-6 py-3 mb-2 text-gray-700 hover:bg-blue-50 transition rounded-r-full mx-2 ${
                location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path + '/')) ? 'bg-blue-100 font-semibold text-blue-700' : ''
              }`}
            >
              <span className="mr-3 text-lg">{link.icon}</span>
              <span className="text-sm lg:text-base">{link.name}</span>
            </Link>
          ))}
          {/* Profile link */}
          <Link
            to="/profile"
            className={`flex items-center px-6 py-3 mb-2 text-gray-700 hover:bg-blue-50 transition rounded-r-full mx-2 ${
              location.pathname.startsWith('/profile') ? 'bg-blue-100 font-semibold text-blue-700' : ''
            }`}
          >
            <span className="mr-3 text-lg"><FaUser /></span>
            <span className="text-sm lg:text-base">Profile</span>
          </Link>
        </nav>
        {canImpersonate && (
          <div className="border-t p-4 space-y-2">
            <div className="flex items-center text-sm font-medium text-gray-700">
              <FaUserSecret className="mr-2" />
              Login as employee
            </div>
            <select
              className="input w-full text-sm"
              value={selectedImpersonateId}
              onChange={(e) => setSelectedImpersonateId(e.target.value)}
            >
              <option value="">Select employee</option>
              {impersonateOptions.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}{emp.drbEmployeeNumber ? ` (${emp.drbEmployeeNumber})` : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-outline btn-sm w-full"
              disabled={!selectedImpersonateId || impersonateLoading}
              onClick={handleImpersonate}
            >
              {impersonateLoading ? 'Switching...' : 'Login as'}
            </button>
          </div>
        )}
        {isImpersonating && (
          <div className="border-t p-4">
            <button
              type="button"
              className="btn btn-primary btn-sm w-full"
              onClick={stopImpersonating}
            >
              Return to administrator
            </button>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-white border-b flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              <FaBars className="w-5 h-5" />
            </button>
            <div className="font-semibold text-lg truncate">HRMS</div>
            {isImpersonating && (
              <span className="hidden sm:inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-800">
                Viewing as {user?.firstName} {user?.lastName}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2 relative z-10" ref={dropdownRef}>
            {user && (
              <button
                type="button"
                onClick={handleOpenClubCorners}
                disabled={clubCornersLoading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100 transition-colors disabled:opacity-60 cursor-pointer relative z-10"
                title="Open Club Corners"
              >
                <FaExternalLinkAlt className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{clubCornersLoading ? 'Opening…' : 'Open Club Corners'}</span>
              </button>
            )}
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <AuthenticatedImage
                src={user?.photoUrl?.startsWith('http') || user?.photoUrl?.startsWith('data:') || user?.photoUrl?.startsWith('blob:') ? user.photoUrl : undefined}
                apiPath={user?.employeeId ? `/employees/${user.employeeId}/photo` : user?.id ? `/employees/${user.id}/photo` : undefined}
                alt={user?.firstName || 'User'}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-gray-200"
                fallback={
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                    {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </div>
                }
              />
              <span className="text-gray-700 font-medium hidden sm:block truncate max-w-32">
                {user?.firstName ? `${user.firstName} ${user.lastName || ''}` : user?.email || 'User'}
              </span>
              <FaChevronDown className={`text-gray-500 transition-transform flex-shrink-0 ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {user?.firstName ? `${user.firstName} ${user.lastName || ''}` : user?.email}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsDropdownOpen(false);
                    handleOpenClubCorners();
                  }}
                  disabled={clubCornersLoading}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-60"
                >
                  <FaExternalLinkAlt className="mr-3 text-gray-400 flex-shrink-0" />
                  {clubCornersLoading ? 'Opening…' : 'Open Club Corners'}
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <FaSignOutAlt className="mr-3 text-gray-400 flex-shrink-0" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </header>
        
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <div className="max-w-6xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 